import React, { useState, useEffect } from 'react';
import {
  Form,
  Alert,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useGlobal } from '../../../Context/GlobalContext';
import { useWorkspace } from '../../../Context/WorkspaceContext';
import { FiSettings, FiAlertTriangle } from 'react-icons/fi';
import { useFeaturePermissions } from '../../../hooks/useWorkspacePermissions';
import { AgentSettingsSkeleton } from '../../../Components/SkeletonLoaders';
import PageHeader from '../../../Components/PageHeader';
import '../agent-settings.css';
import CancelButton from '../../../Components/Buttons/CancelButton';
import DangerButton from '../../../Components/Buttons/DangerButton';
import { OutlinedDangerButton } from '../../../Components/Buttons';
import { useLanguage } from '../../../Context/LanguageContext';

const GeneralSettings = () => {
  const { id: chatbotId } = useParams();
  const { apiUrl } = useGlobal();
  const navigate = useNavigate();
  const { fetchWorkspaceDetails, currentWorkspace } = useWorkspace();
  const { t } = useLanguage();

  // Get permission data
  const { agentSettings: settingsPermissions } = useFeaturePermissions();

  // Default values for General settings
  const defaultGeneral = {
    chatbot_name: '',
    allowUrls: 'all',
    excludedUrls: [],
    active: 'active',
  };

  // Settings state
  const [generalSettings, setGeneralSettings] = useState(defaultGeneral);
  const [lastSavedGeneral, setLastSavedGeneral] = useState(defaultGeneral);

  // Save and error state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Add state for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Function to fetch general settings
  const fetchGeneralSettings = async () => {
    setLoading(true);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(
        `${apiUrl}/api/v1/chatbot/get-chatbot-general-settings`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ chatbot_id: chatbotId })
        }
      );
      if (!response.ok) throw new Error(t('dashboard.settings.generalsettings.errorFetchSettings'));
      const data = await response.json();

      // Update general settings
      const customConfig = data.custom_config || {};
      const fetchedGeneral = {
        chatbot_name: data.chatbot_name || '',
        allowUrls: customConfig.allowUrls || 'all',
        excludedUrls: customConfig.excludedUrls || [],
        active: customConfig.active !== undefined ? (customConfig.active ? 'active' : 'disabled') : 'active',
      };
      setGeneralSettings(fetchedGeneral);
      setLastSavedGeneral(fetchedGeneral);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial loading: fetch general settings
  useEffect(() => {
    fetchGeneralSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, chatbotId]);

  // Helper function to check if settings have changed
  const isGeneralChanged = () =>
    JSON.stringify(generalSettings) !== JSON.stringify(lastSavedGeneral);

  // Save function for General Settings
  const handleSaveGeneral = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const promises = [];

      // Update Chatbot Name if changed
      if (generalSettings.chatbot_name !== lastSavedGeneral.chatbot_name) {
        const p1 = fetch(`${apiUrl}/api/v1/chatbot/update-field`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            chatbot_id: chatbotId,
            field_name: 'chatbot_name',
            field_value: generalSettings.chatbot_name
          }),
        }).then(async (res) => {
          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.message || t('dashboard.settings.generalsettings.errorUpdateName'));
          }
        });
        promises.push(p1);
      }

      // Update Configs (allowUrls, active) if changed
      const configUpdates = {};
      if (generalSettings.allowUrls !== lastSavedGeneral.allowUrls) {
        configUpdates.allowUrls =
          generalSettings.allowUrls === 'all'
            ? 'all'
            : generalSettings.allowUrls
              .split(',')
              .map((u) => u.trim())
              .filter((u) => u);
      }
      if (generalSettings.excludedUrls !== lastSavedGeneral.excludedUrls) {
        configUpdates.excludedUrls = Array.isArray(generalSettings.excludedUrls)
          ? generalSettings.excludedUrls
          : generalSettings.excludedUrls
            .split(',')
            .map((u) => u.trim())
            .filter((u) => u);
      }
      if (generalSettings.active !== lastSavedGeneral.active) {
        configUpdates.active = generalSettings.active === 'active';
      }

      if (Object.keys(configUpdates).length > 0) {
        const requestBody = {
          chatbot_id: chatbotId,
          configs_and_values: configUpdates,
        };
        const p2 = fetch(`${apiUrl}/api/v1/chatbot/update-config`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }).then(async (res) => {
          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || data.message || t('dashboard.settings.generalsettings.errorUpdateConfig'));
          }
        });
        promises.push(p2);
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        setLastSavedGeneral(generalSettings);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        window.dispatchEvent(new CustomEvent('chatbotNameUpdated', { detail: generalSettings.chatbot_name }));
      } else {
        setShowSuccess(false);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
      await fetchWorkspaceDetails(currentWorkspace.workspace_id, true); // Force refresh
    }
  };

  const handleResetGeneral = () => {
    setGeneralSettings(lastSavedGeneral);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const resp = await fetch(`${apiUrl}/api/v1/chatbot/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatbot_id: chatbotId
        }),
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || data.message || t('dashboard.settings.generalsettings.errorDelete'));
      }

      // Refresh workspace data to update chatbot counts and other metrics
      if (currentWorkspace?.workspace_id) {
        try {
          await fetchWorkspaceDetails(currentWorkspace.workspace_id, true); // Force refresh
        } catch (error) {
          // Don't block navigation if workspace refresh fails
        }
      }

      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Create PageHeader component to be used in both skeleton and main render
  const pageHeaderComponent = (
    <PageHeader
      icon={<FiSettings />}
      title={t('dashboard.settings.generalsettings.pageTitle')}
      description={t('dashboard.settings.generalsettings.pageDescription')}
      cancelButton={settingsPermissions.canModify ? {
        children: t('dashboard.settings.generalsettings.resetButton'),
        onClick: handleResetGeneral,
        disabled: isSaving || !isGeneralChanged(),
        variant: "outline-secondary"
      } : null}
      hideDescriptionOnMobile={false}
      saveButton={settingsPermissions.canModify ? {
        children: isSaving ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            {t('dashboard.settings.generalsettings.savingText')}
          </>
        ) : (
          t('dashboard.settings.generalsettings.saveButton')
        ),
        onClick: handleSaveGeneral,
        disabled: isSaving || !isGeneralChanged(),
        variant: "primary"
      } : null}
    />
  );

  if (loading) {
    return <AgentSettingsSkeleton pageHeader={pageHeaderComponent} />;
  }

  return (
    <div className="agent-settings-container">
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger">
            <div className="d-flex align-items-center">
              <FiAlertTriangle className="me-2" size={24} />
              {t('dashboard.settings.generalsettings.deleteModalTitle')}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="mb-0">{t('dashboard.settings.generalsettings.deleteModalMessage')}</p>
          <p className="text-muted mt-2 mb-0 small">{t('dashboard.settings.generalsettings.deleteModalWarning')}</p>
          {error && isDeleting && (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <CancelButton onClick={handleCloseDeleteModal} disabled={isDeleting}>
            {t('dashboard.settings.generalsettings.deleteModalCancelButton')}
          </CancelButton>
          <DangerButton
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('dashboard.settings.generalsettings.deletingText')}
              </>
            ) : (
              t('dashboard.settings.generalsettings.deleteModalDeleteButton')
            )}
          </DangerButton>
        </Modal.Footer>
      </Modal>

      {pageHeaderComponent}

      <div className="settings-content">
        {/* Display general save/fetch errors */}
        {error && !isDeleting && (
          <Alert variant="danger" className="settings-alert">
            {error}
          </Alert>
        )}

        <div className="settings-form-container">
          <Form>
            <div className="settings-form-grid">
              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.generalsettings.bvNameLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canChangeName ? 'permission-restricted' : ''}`}>
                  <Form.Control
                    type="text"
                    value={generalSettings.chatbot_name}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        chatbot_name: e.target.value,
                      })
                    }
                    className="settings-input"
                    maxLength={50}
                    disabled={!settingsPermissions.canChangeName}
                    readOnly={!settingsPermissions.canChangeName}
                  />
                </div>
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.generalsettings.bvNameHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.generalsettings.allowedUrlsLabel')}</Form.Label>
                <div className={`settings-radio-wrapper ${!settingsPermissions.canModifyUrls ? 'permission-restricted' : ''}`}>
                  <Form.Check
                    inline
                    label={t('dashboard.settings.generalsettings.allUrlsOption')}
                    type="radio"
                    name="allowUrlsType"
                    id="allUrls"
                    value="all"
                    checked={generalSettings.allowUrls === 'all'}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        allowUrls: e.target.value,
                      })
                    }
                    className="settings-radio"
                    disabled={!settingsPermissions.canModifyUrls}
                  />
                  <Form.Check
                    inline
                    label={t('dashboard.settings.generalsettings.customUrlsOption')}
                    type="radio"
                    name="allowUrlsType"
                    id="customUrls"
                    value={Array.isArray(generalSettings.allowUrls) ? generalSettings.allowUrls.join(', ') : ''}
                    checked={generalSettings.allowUrls !== 'all'}
                    onChange={() =>
                      setGeneralSettings({
                        ...generalSettings,
                        allowUrls: Array.isArray(lastSavedGeneral.allowUrls) ? lastSavedGeneral.allowUrls.join(', ') : '',
                      })
                    }
                    className="settings-radio"
                    disabled={!settingsPermissions.canModifyUrls}
                  />
                </div>
                {generalSettings.allowUrls !== 'all' && (
                  <>
                    <div className={`settings-textarea-wrapper ${!settingsPermissions.canModifyUrls ? 'permission-restricted' : ''}`}>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={Array.isArray(generalSettings.allowUrls) ? generalSettings.allowUrls.join('\n') : generalSettings.allowUrls}
                        onChange={(e) =>
                          setGeneralSettings({
                            ...generalSettings,
                            allowUrls: e.target.value,
                          })
                        }
                        className="settings-textarea"
                        placeholder={t('dashboard.settings.generalsettings.customUrlsPlaceholder')}
                        disabled={!settingsPermissions.canModifyUrls}
                        readOnly={!settingsPermissions.canModifyUrls}
                      />
                    </div>
                    <Form.Text className="settings-help-text" dangerouslySetInnerHTML={{
                      __html: t('dashboard.settings.generalsettings.customUrlsHelp')
                    }}>
                    </Form.Text>
                  </>
                )}
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.generalsettings.excludedUrlsLabel')}</Form.Label>
                <div className={`settings-textarea-wrapper ${!settingsPermissions.canModifyUrls ? 'permission-restricted' : ''}`}>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={Array.isArray(generalSettings.excludedUrls) ? generalSettings.excludedUrls.join('\n') : generalSettings.excludedUrls}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        excludedUrls: e.target.value,
                      })
                    }
                    className="settings-textarea"
                    placeholder={t('dashboard.settings.generalsettings.excludedUrlsPlaceholder')}
                    disabled={!settingsPermissions.canModifyUrls}
                    readOnly={!settingsPermissions.canModifyUrls}
                  />
                </div>
                <Form.Text className="settings-help-text" dangerouslySetInnerHTML={{
                  __html: t('dashboard.settings.generalsettings.excludedUrlsHelp')
                }}>
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.generalsettings.statusLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canChangeStatus ? 'permission-restricted' : ''}`}>
                  <Form.Select
                    value={generalSettings.active}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        active: e.target.value,
                      })
                    }
                    className="settings-select"
                    disabled={!settingsPermissions.canChangeStatus}
                  >
                    <option value="active">{t('dashboard.settings.generalsettings.statusActiveOption')}</option>
                    <option value="disabled">{t('dashboard.settings.generalsettings.statusDisabledOption')}</option>
                  </Form.Select>
                </div>
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.generalsettings.statusHelp')}
                </Form.Text>
              </div>
            </div>
          </Form>

        </div>

        {showSuccess && (
          <Alert variant="success" className="settings-success-alert">
            {t('dashboard.settings.generalsettings.successSaved')}
          </Alert>
        )}

        {/* Delete Button Section */}
        {settingsPermissions.canDeleteAgent && (
          <div className="settings-danger-section">
            <div className="settings-danger-card">
              <div className="settings-danger-header">
                <div className="settings-danger-icon">
                  <FiAlertTriangle />
                </div>
                <div className="settings-danger-content">
                  <h5>{t('dashboard.settings.generalsettings.deleteBvTitle')}</h5>
                  <p>{t('dashboard.settings.generalsettings.deleteBvDescription')}</p>
                </div>
              </div>
              <div className="settings-danger-footer">
                <OutlinedDangerButton
                  mobileWidth="100%"
                  onClick={handleDeleteClick}
                  disabled={loading || isDeleting}
                >
                  <FiAlertTriangle className="me-2" />
                  {t('dashboard.settings.generalsettings.deleteBvButton')}
                </OutlinedDangerButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSettings;